"use client";

import { Bell, ExternalLink, X } from "lucide-react";
import { useNotifications, useMarkNotificationRead } from "@/hooks/use-notifications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "next/navigation";

/**
 * Notification bell with unread count badge and dropdown.
 *
 * Shows recent notifications. Clicking a notification with a link navigates
 * to it and marks as read. Notifications without links can be dismissed
 * with the X button.
 */
export function NotificationBell() {
  const router = useRouter();
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();

  const notifications = data?.notifications || [];
  const unreadCount = notifications.length;

  function handleNavigate(notificationId: string, linkUrl: string) {
    markRead.mutate(notificationId);
    router.push(linkUrl);
  }

  function handleDismiss(e: React.MouseEvent, notificationId: string) {
    e.stopPropagation();
    markRead.mutate(notificationId);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px]"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="p-3 border-b flex items-center justify-between">
          <h4 className="text-sm font-bold">Notifications</h4>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">{unreadCount} unread</span>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No new notifications
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-96">
            {notifications.map((notification, index) => (
              <div key={notification.id}>
                <div className="flex items-start gap-2 p-3 hover:bg-accent transition-colors group">
                  {/* Content — clickable if has link */}
                  {notification.LinkURL ? (
                    <button
                      className="flex-1 text-left min-w-0"
                      onClick={() => handleNavigate(notification.id, notification.LinkURL!)}
                    >
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{notification.Title}</p>
                        <ExternalLink className="h-3 w-3 text-primary shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.Message}
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {formatTimestamp(notification.CreatedAt)}
                      </p>
                    </button>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{notification.Title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.Message}
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {formatTimestamp(notification.CreatedAt)}
                      </p>
                    </div>
                  )}

                  {/* Dismiss button */}
                  <button
                    className="shrink-0 p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleDismiss(e, notification.id)}
                    title="Dismiss"
                  >
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
                {index < notifications.length - 1 && <Separator />}
              </div>
            ))}
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
